import { useState } from "react";
import { toast } from "sonner";
import { Flag, RotateCcw } from "lucide-react";
import { api, errMsg, useApi } from "../lib/api";
import { fmtClock } from "../lib/labels";
import { Button } from "./ui/button";

const STATUS = {
  capturing: { label: "A conquistar", cls: "border-emerald-500/40 text-emerald-300" },
  contested: { label: "Contestada", cls: "border-rose-500/40 text-rose-300" },
  standby: { label: "Em espera", cls: "border-slate-600 text-slate-400" },
};

const Row = ({ c, onReset, busy }) => {
  const s = STATUS[c.status] || STATUS.standby;
  return (
    <div className="border-b border-slate-800/70 py-3 last:border-0" data-testid={`capture-row-${c.zone.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-slate-100">{c.zone.name}</div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-slate-500">
            <span style={{ color: c.org?.color }}>{c.org?.label}</span> · dono: {c.owner?.label || "sem dono"}
          </div>
        </div>
        <span className={`border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${s.cls}`} data-testid={`capture-status-${c.zone.id}`}>{s.label}</span>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => onReset(c)} data-testid={`capture-reset-${c.zone.id}`} className="text-rose-400 hover:text-rose-300">
          <RotateCcw className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <div className="h-1.5 flex-1 overflow-hidden bg-slate-800">
          <div className="h-full transition-[width] duration-700" style={{ width: `${c.progress}%`, background: c.org?.color || "#10b981" }} />
        </div>
        <span className="w-14 text-right font-mono text-xs text-slate-200" data-testid={`capture-progress-${c.zone.id}`}>{Number(c.progress).toFixed(1)}%</span>
      </div>
      <div className="mt-1 font-mono text-[10px] text-slate-500">
        {c.inside} membro(s) na zona{c.eta_seconds != null ? ` · faltam ~${fmtClock(c.eta_seconds)}` : ""}
      </div>
    </div>
  );
};

export const ActiveCaptures = () => {
  const { data, reload } = useApi("/admin/captures", 5000);
  const [busy, setBusy] = useState(false);
  const reset = async (c) => {
    if (!window.confirm(`Repor a conquista de ${c.zone.name} (${c.org?.label}, ${Number(c.progress).toFixed(1)}%)? A barra volta a zero.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/captures/${c.zone.id}`);
      toast.success("Conquista reposta.");
      reload();
    } catch (e) {
      toast.error(errMsg(e, "Não foi possível repor a conquista."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="panel fade-up p-5" data-testid="active-captures">
      <h2 className="mb-2 flex items-center gap-2 font-display text-sm uppercase tracking-wider"><Flag className="h-4 w-4 text-emerald-400" />Conquistas ativas</h2>
      {data && data.length === 0 && <div className="py-4 text-sm text-slate-500" data-testid="active-captures-empty">Nenhuma conquista em curso.</div>}
      {(data || []).map((c) => <Row key={c.zone.id} c={c} onReset={reset} busy={busy} />)}
    </div>
  );
};
